import { z } from 'zod';
import { ConceptSelectionSchema } from './prompt-blocks.js';
// ============================================================
// AI Generation Schemas
// ============================================================
export const AIStyleSchema = z.enum([
    'CLASSIC',
    'MODERN',
    'VINTAGE',
    'ROMANTIC',
    'CINEMATIC',
    'CLASSIC_STUDIO',
    'OUTDOOR_GARDEN',
    'SUNSET_BEACH',
    'TRADITIONAL_HANBOK',
    'VINTAGE_CINEMATIC',
    'LUXURY_HOTEL',
    'CITY_LIFESTYLE',
    'ENCHANTED_FOREST',
    'BLACK_AND_WHITE',
    'MINIMALIST_GALLERY',
]);
export const AIGenerationStatusSchema = z.enum([
    'PENDING',
    'PROCESSING',
    'COMPLETED',
    'FAILED',
]);
export const AIGenerationSchema = z.object({
    id: z.cuid2(),
    userId: z.cuid2(),
    originalUrl: z.url().max(500),
    style: AIStyleSchema,
    generatedUrls: z.array(z.url()).nullable(),
    selectedUrl: z.url().max(500).nullable(),
    status: AIGenerationStatusSchema.default('PENDING'),
    creditsUsed: z.number().int().min(1).default(1),
    cost: z.number().min(0),
    replicateId: z.string().max(255).nullable(),
    providerJobId: z.string().max(255).nullable(),
    providerType: z.enum(['replicate', 'openai', 'gemini']).nullable(),
    createdAt: z.date(),
    completedAt: z.date().nullable(),
});
// ============================================================
// API Request/Response
// ============================================================
export const GenerateAIPhotoServerRequestSchema = z.object({
    style: AIStyleSchema,
    imageUrl: z.url(),
});
export const SelectAIPhotoRequestSchema = z.object({
    generationId: z.cuid2(),
    selectedUrl: z.url(),
});
export const AIGenerationResponseSchema = AIGenerationSchema.omit({
    userId: true,
    cost: true,
    replicateId: true,
    providerJobId: true,
    providerType: true,
});
export const AIGenerationStatusResponseSchema = z.object({
    id: z.cuid2(),
    status: AIGenerationStatusSchema,
    generatedUrls: z.array(z.url()).nullable(),
    progress: z.number().min(0).max(100).optional(),
    errorMessage: z.string().optional(),
});
export const AICreditsResponseSchema = z.object({
    userId: z.cuid2(),
    aiCredits: z.number().int().min(0),
    premiumPlan: z.enum(['FREE', 'PREMIUM']),
    totalGenerated: z.number().int().min(0),
    canGenerate: z.boolean(),
});
export const ToggleFavoriteSchema = z.object({
    isFavorited: z.boolean(),
});
export const UpdateGenerationSchema = z.object({
    isFavorited: z.boolean().optional(),
    favoritedUrls: z.array(z.string()).optional(),
    captions: z.record(z.string(), z.string().max(200)).optional(),
}).refine(data => data.isFavorited !== undefined || data.favoritedUrls !== undefined || data.captions !== undefined, {
    message: '최소 하나의 필드를 제공해야 합니다',
});
export const MovePhotoSchema = z.object({
    generationId: z.string(),
    url: z.string(),
    targetAlbumId: z.string(),
});
// ============================================================
// Album Schemas
// ============================================================
export const CreateAlbumSchema = z.object({
    name: z.string().min(1).max(255),
    snapType: z.enum(['STUDIO', 'OUTDOOR', 'CONCEPT']).optional(),
});
export const AlbumGroupSchema = z.object({
    id: z.string(),
    name: z.string().min(1).max(100),
    sortOrder: z.number().int().min(0),
    isDefault: z.boolean().optional(),
});
export const AlbumImageSchema = z.object({
    url: z.url(),
    generationId: z.string(),
    style: z.string(),
    role: z.enum(['GROOM', 'BRIDE', 'COUPLE']),
    sortOrder: z.number().int().min(0),
    groupId: z.string().optional(),
    tags: z.array(z.string().max(30)).max(10).optional(),
});
export const UpdateAlbumSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    groups: z.array(AlbumGroupSchema).max(20).optional(),
    images: z.array(AlbumImageSchema).max(50).optional(),
});
export const ApplyAlbumSchema = z.object({
    invitationId: z.string().min(1),
});
// ============================================================
// Reference Photo Schemas
// ============================================================
export const CreateReferencePhotoSchema = z.object({
    role: z.enum(['GROOM', 'BRIDE']),
});
export const ReferencePhotoSchema = z.object({
    id: z.string(),
    role: z.enum(['GROOM', 'BRIDE']),
    originalUrl: z.url(),
    faceDetected: z.boolean(),
    isActive: z.boolean(),
    createdAt: z.date(),
});
// ============================================================
// Job Schemas
// ============================================================
export const JobModeSchema = z.enum(['SINGLE', 'BATCH']);
export const JobStatusSchema = z.enum([
    'PENDING', 'PROCESSING', 'COMPLETED', 'PARTIAL', 'FAILED', 'CANCELLED',
]);
export const CreateJobSchema = z.object({
    albumId: z.string().min(1),
    mode: JobModeSchema,
    styles: z.array(z.string()).min(1),
    roles: z.array(z.enum(['GROOM', 'BRIDE'])).min(1),
    modelId: z.string().optional(),
    totalImages: z.number().int().min(1).max(20),
    referencePhotoIds: z.array(z.string()).min(1),
});
export const JobTaskSchema = z.object({
    index: z.number().int().min(0),
    style: z.string(),
    role: z.enum(['GROOM', 'BRIDE', 'COUPLE']),
    referencePhotoIds: z.array(z.string()).min(1),
});
export const JobResponseSchema = z.object({
    id: z.string(),
    mode: JobModeSchema,
    totalImages: z.number(),
    completedImages: z.number(),
    failedImages: z.number(),
    creditsReserved: z.number(),
    creditsUsed: z.number(),
    status: JobStatusSchema,
    tasks: z.array(JobTaskSchema).optional(),
    createdAt: z.date(),
});
// ============================================================
// Credit Transaction Schemas
// ============================================================
export const CreditTxTypeSchema = z.enum(['DEDUCT', 'REFUND', 'PURCHASE', 'BONUS']);
export const CreditTransactionSchema = z.object({
    id: z.string(),
    type: CreditTxTypeSchema,
    amount: z.number().int(),
    balanceAfter: z.number().int(),
    referenceType: z.string().nullable(),
    referenceId: z.string().nullable(),
    description: z.string().nullable(),
    createdAt: z.date(),
});
export const CreditBalanceResponseSchema = z.object({
    balance: z.number().int().min(0),
    transactions: z.array(CreditTransactionSchema),
});
// ============================================================
// SSE Stream Event Schemas
// ============================================================
export const SSEStatusEventSchema = z.object({
    type: z.literal('status'),
    message: z.string(),
});
export const SSEImageEventSchema = z.object({
    type: z.literal('image'),
    index: z.number().int().min(0),
    url: z.url(),
    progress: z.number().int().min(0),
    total: z.number().int().min(1),
});
export const SSEDoneEventSchema = z.object({
    type: z.literal('done'),
    id: z.string(),
    originalUrl: z.url(),
    generatedUrls: z.array(z.url()),
    style: z.string(),
    albumId: z.string().nullable(),
    remainingCredits: z.number().int().min(0),
    jobProgress: z.object({
        completed: z.number().int(),
        total: z.number().int(),
    }).optional(),
});
export const SSEErrorEventSchema = z.object({
    type: z.literal('error'),
    error: z.string(),
});
export const SSEEventSchema = z.discriminatedUnion('type', [
    SSEStatusEventSchema,
    SSEImageEventSchema,
    SSEDoneEventSchema,
    SSEErrorEventSchema,
]);
// ============================================================
// Studio Generate (스튜디오 AI 생성)
// ============================================================
export const StudioGenerateSchema = z
    .object({
    // 참조 이미지 입력 — 둘 중 하나는 필수.
    // imageUrls: data:URL (즉석 업로드) 또는 S3 URL 직접 전달
    // referencePhotoIds: aiReferencePhotos 에 사전 업로드된 행 ID 재사용
    imageUrls: z.array(z.string()).min(1).max(5).optional(),
    referencePhotoIds: z.array(z.string()).min(1).max(5).optional(),
    modelId: z.string().optional(),
    style: z.string().optional(),
    role: z.enum(['GROOM', 'BRIDE', 'COUPLE']).default('GROOM'),
    customPrompt: z.string().optional(),
    count: z.number().int().min(1).max(10).default(1),
    promptBlocks: ConceptSelectionSchema.optional(),
    albumId: z.string().optional(),
    albumName: z.string().max(255).optional(),
    // 멱등성 키 — 클라이언트가 생성해서 보낸다. 같은 키로 두 번 들어오면 첫 잡을 재사용.
    // UUID(36자) 또는 cuid2(20~32자) 둘 다 허용. 영숫자+하이픈만.
    clientJobId: z
        .string()
        .min(20)
        .max(128)
        .regex(/^[a-zA-Z0-9-]+$/)
        .optional(),
})
    .refine((data) => (data.imageUrls && data.imageUrls.length > 0) || (data.referencePhotoIds && data.referencePhotoIds.length > 0), { message: 'imageUrls 또는 referencePhotoIds 중 하나는 필수입니다', path: ['imageUrls'] });
// ============================================================
// Download Schemas
// ============================================================
export const DownloadPhotosSchema = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('selected'), urls: z.array(z.string()).min(1).max(20), label: z.string().max(100).optional() }),
    z.object({ mode: z.literal('album'), albumId: z.string() }),
    z.object({ mode: z.literal('all') }),
    z.object({ mode: z.literal('favorites') }),
]);
// 셀프 "이메일로 받기" (FIKET-479) — 인앱 브라우저에서 저장이 안 되는 고객이 20장 단위 zip 링크(7일)를 이메일로 받는다.
// 수신 주소는 기본 계정 이메일(users.email). `email` 을 보내면 그 주소로 간다(고객이 확인 시트에서 바꾼 경우) — 계정 이메일이
// 소셜 로그인 계정과 달라 메일함을 못 찾는 고객용. 남용은 라우트의 봇 가드(분당·하루 상한)가 막는다. POST /api/ai/photos/email
const EmailPhotosRecipientSchema = z
    .string()
    .trim()
    .toLowerCase()
    .email('올바른 이메일을 입력하세요')
    .max(255)
    .optional();
export const EmailPhotosRequestSchema = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('all'), email: EmailPhotosRecipientSchema }),
    z.object({ mode: z.literal('selected'), urls: z.array(z.string()).min(1).max(200), email: EmailPhotosRecipientSchema }),
]);
export const EmailPhotosResponseSchema = z.object({
    /** 마스킹한 수신 주소 (예: ab***@gmail.com) */
    sentTo: z.string(),
    photoCount: z.number().int(),
    zipCount: z.number().int(),
    /** 링크 만료 시각 (ISO) */
    expiresAt: z.string(),
});
export const SSEStudioGenDoneEventSchema = z.object({
    type: z.literal('done'),
    generationId: z.string(),
    urls: z.array(z.string()),
    remainingCredits: z.number().int().min(0),
    creditsUsed: z.number().int().min(0),
    successCount: z.number().int().min(0),
    failureCount: z.number().int().min(0),
    modelId: z.string(),
    prompt: z.string(),
    albumId: z.string().optional(),
});
//# sourceMappingURL=ai.js.map